import type { GetStaticProps } from 'next'
import type { CSSProperties, FC, ReactNode } from 'react'
import type { Advisor } from '@/types'
import type {
    FormatPreference,
    GoalId,
    MatchAnswers,
    StageId,
    TimePreference
} from '@/lib/match'

import Link from 'next/link'
import { useMemo, useRef, useState } from 'react'
import { ArrowLeft, Check, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Landscape } from '@/components/generals/landscape'
import { PillButton } from '@/components/generals/pill-button'
import { RatingChip } from '@/components/generals/rating-chip'
import { SplitHeading } from '@/components/generals/split-heading'
import { WashedPortrait } from '@/components/generals/washed-portrait'
import { AdvisorCard } from '@/components/landing/advisors/advisor-card'
import { getAdvisors } from '@/lib/advisors'
import { LANDSCAPES } from '@/lib/visual'

import {
    FORMATS,
    GOALS,
    matchAdvisors,
    STAGES,
    TIMES
} from '@/lib/match'

interface FindAdvisorProps {
    advisors: Array<Advisor>
}

interface OptionProps {
    label: string
    detail?: string
    selected: boolean
    onSelect: () => void
    compact?: boolean
}

const Option = ({ label, detail, selected, onSelect, compact = false }: OptionProps) => {
    return (
        <button
            type='button'
            role='radio'
            aria-checked={selected}
            onClick={onSelect}
            className={cn(
                'group flex w-full items-center gap-4 rounded-[20px] text-left transition-all duration-300',
                compact ? 'px-4 py-3' : 'px-5 py-4',
                selected ? 'bg-cream text-black' : 'bg-white/[0.04] text-cream ring-1 ring-white/10 hover:bg-white/[0.07] hover:ring-white/20'
            )}
        >
            <span
                aria-hidden='true'
                className={cn(
                    'grid size-5 shrink-0 place-items-center rounded-full transition-colors',
                    selected ? 'bg-black text-cream' : 'ring-1 ring-white/30'
                )}
            >
                {selected && <Check className='size-3' strokeWidth={3} />}
            </span>

            <span className='min-w-0'>
                <span className={cn('block tracking-[-0.01em]', compact ? 'text-sm' : 'text-[15px]')}>
                    {label}
                </span>

                {detail && (
                    <span className={cn('mt-0.5 block text-xs', selected ? 'text-black/60' : 'text-cream/50')}>
                        {detail}
                    </span>
                )}
            </span>
        </button>
    )
}

const STEP_TITLES = ['Which goal *comes first?*', 'Where are things *right now?*', 'When and how *suits you?*']

const FindAdvisorPage: FC<FindAdvisorProps> = ({ advisors }): ReactNode => {
    const [step, setStep] = useState(0)
    const [goal, setGoal] = useState<GoalId | null>(null)
    const [stage, setStage] = useState<StageId | null>(null)
    const [time, setTime] = useState<TimePreference>('Any time')
    const [format, setFormat] = useState<FormatPreference>('No preference')
    const [finished, setFinished] = useState(false)
    const cardRef = useRef<HTMLDivElement>(null)

    const matches = useMemo(() => {
        if (!finished || !goal || !stage) return []

        const answers: MatchAnswers = { goal, stage, time, format }

        return matchAdvisors(advisors, answers)
    }, [finished, goal, stage, time, format, advisors])

    const canContinue = (step === 0 && goal !== null) || (step === 1 && stage !== null) || step === 2
    const progress = finished ? 3 : step

    const focusCard = () => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    const next = () => {
        if (!canContinue) return

        if (step < 2) setStep(step + 1)
        else setFinished(true)

        focusCard()
    }

    const back = () => {
        if (finished) setFinished(false)
        else setStep(Math.max(0, step - 1))
    }

    const restart = () => {
        setStep(0)
        setGoal(null)
        setStage(null)
        setTime('Any time')
        setFormat('No preference')
        setFinished(false)
        focusCard()
    }

    const best = matches[0]
    const alternatives = matches.slice(1, 3)

    return (
        <Layout>
            <Seo
                title='Find an advisor'
                description='Three short questions about your goal, your stage and your week, and Verdane suggests the advisor who suits you best. Your first call is free.'
            />

            <section aria-labelledby='finder-title' className='relative isolate min-h-screen overflow-hidden pb-24 pt-32 sm:pt-40'>
                <div aria-hidden='true' className='fixed inset-0 -z-10'>
                    <Landscape src={LANDSCAPES.fieldDusk} priority fade='none' position='center 55%' />
                    <div className='absolute inset-0 bg-black/55' />
                    <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.65)_0%,rgba(0,0,0,0.2)_40%,rgba(0,0,0,0.85)_100%)]' />
                </div>

                <div className='shell flex flex-col items-center'>
                    <Eyebrow>
                        Find your advisor
                    </Eyebrow>

                    <SplitHeading
                        as='h1'
                        id='finder-title'
                        trigger='mount'
                        delay={150}
                        text='A few answers, *the right advisor*'
                        className='mt-6 text-balance text-center text-[2.6rem] font-normal leading-[1] tracking-[-0.045em] text-cream sm:text-6xl'
                    />

                    <div ref={cardRef} className='mt-12 w-full max-w-2xl scroll-mt-28'>
                        <div className='glass-dark grain motion-rise relative overflow-hidden rounded-[32px] p-5 sm:p-8 [--rise-delay:400ms]'>
                            <div className='flex items-center justify-between gap-4 text-xs text-cream/55'>
                                <span>
                                    {finished ? 'Your result' : `Question ${step + 1} of 3`}
                                </span>

                                {(step > 0 || finished) && (
                                    <button type='button' onClick={back} className='inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors hover:text-cream'>
                                        <ArrowLeft aria-hidden='true' className='size-3.5' />
                                        Go back
                                    </button>
                                )}
                            </div>

                            <div
                                role='progressbar'
                                aria-label='Your progress'
                                aria-valuemin={0}
                                aria-valuemax={3}
                                aria-valuenow={progress}
                                className='mt-4 grid grid-cols-3 gap-1.5'
                            >
                                {[0, 1, 2].map(index => (
                                    <span key={index} className='h-1 overflow-hidden rounded-full bg-white/10'>
                                        <span
                                            className='block h-full rounded-full bg-[linear-gradient(90deg,#e2f37a,#8fae62)] transition-[width] duration-700 ease-cinema'
                                            style={{ width: index < progress ? '100%' : index === progress && !finished ? '35%' : '0%' }}
                                        />
                                    </span>
                                ))}
                            </div>

                            {!finished && (
                                <div key={step} className='animate-in fade-in slide-in-from-bottom-3 duration-700'>
                                    <SplitHeading
                                        as='h2'
                                        trigger='mount'
                                        text={STEP_TITLES[step]}
                                        className='mt-8 text-3xl font-normal leading-tight tracking-[-0.035em] text-cream sm:text-4xl'
                                    />

                                    {step === 0 && (
                                        <div role='radiogroup' aria-label='Main goal' className='mt-6 grid gap-2.5'>
                                            {GOALS.map(option => (
                                                <Option key={option.id} label={option.label} detail={option.detail} selected={goal === option.id} onSelect={() => setGoal(option.id)} />
                                            ))}
                                        </div>
                                    )}

                                    {step === 1 && (
                                        <div role='radiogroup' aria-label='Where you are now' className='mt-6 grid gap-2.5 sm:grid-cols-2'>
                                            {STAGES.map(option => (
                                                <Option key={option.id} label={option.label} detail={option.detail} selected={stage === option.id} onSelect={() => setStage(option.id)} />
                                            ))}
                                        </div>
                                    )}

                                    {step === 2 && (
                                        <div className='mt-6 space-y-7'>
                                            <div>
                                                <p id='finder-time' className='text-[11px] font-medium uppercase tracking-[0.24em] text-cream/50'>
                                                    Best times for you
                                                </p>

                                                <div role='radiogroup' aria-labelledby='finder-time' className='mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3'>
                                                    {TIMES.map(option => (
                                                        <Option key={option} compact label={option} selected={time === option} onSelect={() => setTime(option)} />
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <p id='finder-format' className='text-[11px] font-medium uppercase tracking-[0.24em] text-cream/50'>
                                                    Preferred way to meet
                                                </p>

                                                <div role='radiogroup' aria-labelledby='finder-format' className='mt-3 grid grid-cols-2 gap-2'>
                                                    {FORMATS.map(option => (
                                                        <Option key={option} compact label={option} selected={format === option} onSelect={() => setFormat(option)} />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className='mt-8 flex items-center justify-between gap-4'>
                                        <p className='text-xs text-cream/45'>
                                            Nothing you choose here is saved.
                                        </p>

                                        <PillButton onClick={next} disabled={!canContinue}>
                                            {step < 2 ? 'Next' : 'Show my advisor'}
                                        </PillButton>
                                    </div>
                                </div>
                            )}

                            {finished && best && (
                                <div className='animate-in fade-in slide-in-from-bottom-4 duration-1000' aria-live='polite'>
                                    <div className='mt-8 grid items-stretch gap-6 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)]'>
                                        <div className='grain relative isolate aspect-[3/4] overflow-hidden rounded-[26px] bg-ink'>
                                            {best.advisor.portrait && (
                                                <WashedPortrait
                                                    src={best.advisor.portrait}
                                                    alt={best.advisor.portraitAlt}
                                                    wash={best.advisor.washColor}
                                                    intensity='medium'
                                                    sizes='(min-width: 640px) 300px, 90vw'
                                                />
                                            )}
                                            <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_50%,rgba(0,0,0,0.7)_100%)]' />
                                            <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

                                            <span className='absolute left-4 top-4 rounded-full bg-citron px-3 py-1 text-xs font-medium text-black'>
                                                {best.score}% fit
                                            </span>

                                            <RatingChip rating={best.advisor.rating} reviews={best.advisor.reviewCount} className='absolute bottom-4 left-4' />
                                        </div>

                                        <div className='flex flex-col'>
                                            <p className='text-[11px] font-medium uppercase tracking-[0.28em] text-cream/50'>
                                                Our suggestion for you
                                            </p>

                                            <h2 className='mt-3 font-serif text-5xl italic leading-[0.95] text-cream'>
                                                {best.advisor.name}
                                            </h2>

                                            <p className='mt-2 text-sm text-cream/55'>
                                                {best.advisor.title}, {best.advisor.location}
                                            </p>

                                            <p className='mt-5 text-[15px] leading-relaxed text-cream/80'>
                                                {best.advisor.shortBio}
                                            </p>

                                            <ul className='mt-5 flex flex-wrap gap-2'>
                                                {best.reasons.map(reason => (
                                                    <li key={reason} className='inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1.5 text-xs text-cream/85 ring-1 ring-white/10'>
                                                        <Check aria-hidden='true' className='size-3 text-citron' strokeWidth={3} />
                                                        {reason}
                                                    </li>
                                                ))}
                                            </ul>

                                            <div className='mt-auto flex flex-wrap items-center gap-4 pt-7'>
                                                <PillButton href={`/advisors/${best.advisor.slug}#book`}>
                                                    Book a free call
                                                </PillButton>

                                                <Link href={`/advisors/${best.advisor.slug}`} className='text-sm text-cream/75 underline decoration-cream/30 underline-offset-[6px] hover:text-cream hover:decoration-citron'>
                                                    Read the full profile
                                                </Link>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='mt-8 flex justify-center'>
                                        <button type='button' onClick={restart} className='inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-cream/60 transition-colors hover:bg-white/5 hover:text-cream'>
                                            <RotateCcw aria-hidden='true' className='size-4' />
                                            Try different answers
                                        </button>
                                    </div>
                                </div>
                            )}

                            {finished && !best && (
                                <div className='mt-8 text-center'>
                                    <p className='font-serif text-3xl italic text-cream'>
                                        We will introduce you ourselves
                                    </p>

                                    <p className='mx-auto mt-3 max-w-md text-sm leading-relaxed text-cream/60'>
                                        The advisor list is not loading at the moment. Write to us and someone on the team will make the introduction for you.
                                    </p>

                                    <PillButton href='/contact' className='mt-6'>
                                        Get in touch
                                    </PillButton>
                                </div>
                            )}
                        </div>
                    </div>

                    {finished && alternatives.length > 0 && (
                        <div className='mt-16 w-full max-w-4xl'>
                            <p className='text-center text-[11px] font-medium uppercase tracking-[0.3em] text-cream/50'>
                                Also worth meeting
                            </p>

                            <ul className='mt-6 grid gap-5 sm:grid-cols-2'>
                                {alternatives.map((match, index) => (
                                    <li key={match.advisor.slug} className='motion-rise' style={{ '--rise-delay': `${300 + index * 150}ms` } as CSSProperties}>
                                        <AdvisorCard advisor={match.advisor} compact />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<FindAdvisorProps> = async () => {
    try {
        const advisors = await getAdvisors()

        return { props: { advisors }, revalidate: 60 }
    }
    catch (error) {
        console.error('Advisors could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { advisors: [] }, revalidate: 15 }
    }
}

export default FindAdvisorPage
